/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { TUsers } from 'integration/resources/newOpportunities'
import { useGetOpportunitiesV3FiltersQuery } from 'integration/resources/opportunitiesV3'
import { useBreakpointValue } from 'native-base'
import { useForm } from 'react-hook-form'
import {
  defaultFilters,
  useOpportunitiesV3FiltersAtom
} from 'src/store/screens/opportunitiesV3Filters'
import { setUsersDisplayMap } from 'src/utils/usersDisplay'

import { V3FiltersSchema } from './constants'
import {
  TFieldConfig,
  TFieldNames,
  TFormFields,
  UseOpportunitiesFilterScreen
} from './OpportunitiesFilterScreen.types'

export const useOpportunitiesFilterScreen: UseOpportunitiesFilterScreen = ({ navigation }) => {
  const isMobile = useBreakpointValue({ base: true, lg: false })

  const [filtersState, setFiltersState] = useOpportunitiesV3FiltersAtom()

  const {
    data: filters,
    isLoading: isLoadingFilter,
    isRefetching: isRefetchingFilter
  } = useGetOpportunitiesV3FiltersQuery()

  const closeRightSheet = useCallback(() => {
    navigation.goBack()
  }, [navigation])

  const { control, formState, reset, handleSubmit, watch } = useForm<TFormFields>({
    resolver: yupResolver(V3FiltersSchema),
    mode: 'onChange',
    defaultValues: {
      priority: filtersState.priority ?? [],
      offer: filtersState.offer ?? [],
      public_rating: filtersState.public_rating ?? [],
      fidelity: filtersState.fidelity ?? [],
      segment: filtersState.segment ?? [],
      network: filtersState.network ?? [],
      subsidiary: filtersState.subsidiary ?? [],
      user_std_code: filtersState.user_std_code ?? []
    }
  })

  const handleSubmitForm = (values: TFormFields) => {
    setFiltersState({
      ...filtersState,
      ...values
    })

    closeRightSheet()
  }

  const handleResetForm = () => {
    setFiltersState(defaultFilters)

    reset(
      {
        priority: [],
        offer: [],
        public_rating: [],
        fidelity: [],
        segment: [],
        network: [],
        subsidiary: [],
        user_std_code: []
      },
      { keepDefaultValues: true }
    )
  }

  const fieldNames: TFieldNames[] = [
    'priority',
    'offer',
    'public_rating',
    'fidelity',
    'segment',
    'network',
    'subsidiary',
    'user_std_code'
  ]

  const filtersList = useMemo(() => filters?.data?.data, [filters])

  const [subsidiariesList, setSubsidiariesList] = useState<string[]>()

  const subsidiaries = useMemo(() => subsidiariesList, [subsidiariesList])

  const networksList = filtersList?.networks.map((item) => item.network)

  const [usersList, setUsersList] = useState<TUsers[]>()

  const users = useMemo(() => usersList, [usersList])

  const PAGE_SIZE = 20

  const [userPage, setUserPage] = useState(1)

  const usersPaginated = useMemo(
    () => (users ? users.slice(0, userPage * PAGE_SIZE) : []),
    [users, userPage]
  )

  const hasMoreUsers = useMemo(
    () => (users?.length || 0) > usersPaginated.length,
    [users?.length, usersPaginated.length]
  )

  const loadMoreUsers = () => {
    setUserPage((p) => p + 1)
  }

  const formFields = watch()

  const allSubsidiaries = filtersList?.networks.reduce(
    //@ts-ignore
    (acc, actual) => acc.concat(actual['subsidiaries'] || []),
    []
  )

  useEffect(() => {
    if (filtersList?.users?.length) {
      setUsersList(filtersList?.users)

      setUsersDisplayMap(filtersList.users).catch(() => {})
    }
  }, [filtersList?.users])

  useEffect(() => {
    const selectedNetworks = formFields.network ?? []

    if (selectedNetworks.length) {
      const subs = (filtersList?.networks || [])
        .filter((item) => selectedNetworks.includes(item.network))
        .reduce<string[]>((acc, n) => acc.concat(n.subsidiaries || []), [])

      setSubsidiariesList(subs)
    } else {
      setSubsidiariesList(allSubsidiaries)
    }

    const selectedSubsidiaries = formFields.subsidiary ?? []

    const baseUsers = filtersList?.users ?? []

    const filteredUsers = baseUsers.filter((u) => {
      const matchNetwork = selectedNetworks.length
        ? selectedNetworks.includes(u.network_name || '')
        : true

      const matchSubs = selectedSubsidiaries.length
        ? selectedSubsidiaries.includes(u.subsidiary_name || '')
        : true

      return matchNetwork && matchSubs
    })

    setUsersList(filteredUsers)

    setUserPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formFields.network, formFields.subsidiary, filtersList?.networks, filtersList?.users])

  const fieldConfig: TFieldConfig = {
    priority: {
      options: filtersList?.priorities ?? [],
      label: 'Prioridade'
    },
    offer: {
      options: filtersList?.offers ?? [],
      label: 'Oferta'
    },
    public_rating: {
      options: filtersList?.public_ratings ?? [],
      label: 'Público'
    },
    fidelity: {
      options: filtersList?.fidelities ?? [],
      label: 'Fidelidade'
    },
    segment: {
      options: filtersList?.segments ?? [],
      label: 'Segmento'
    },
    network: {
      options: networksList ?? [],
      label: 'Rede'
    },
    subsidiary: {
      options: subsidiaries ?? [],
      label: 'Filial'
    },
    user_std_code: {
      options: usersPaginated ?? [],
      label: 'Especialista'
    }
  }

  return {
    closeRightSheet,
    isMobile,
    control,
    handleSubmit,
    handleSubmitForm,
    handleResetForm,
    fieldNames,
    fieldConfig,
    isDirty: formState.isDirty,
    isLoading: isLoadingFilter || isRefetchingFilter,
    hasMoreUsers,
    loadMoreUsers
  }
}
